import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, Wallet, FileQuestion, History } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatCard } from '../../components/shared/StatCard';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatCount } from '../../lib/formatNumber';
import cloudAssetService, {
  CloudCostBreakdownRow,
  CloudCostSummary,
} from '../../services/cloudAssetService';

const BreakdownTable: React.FC<{
  title: string;
  rows: CloudCostBreakdownRow[];
  format: (amount: number) => string;
}> = ({ title, rows, format }) => (
  <Card>
    <CardContent className="p-0">
      <h3 className="border-b px-4 py-3 text-sm font-semibold">{title}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Resources</TableHead>
            <TableHead className="text-right">Monthly Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                Nothing with a cost recorded yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                <TableCell className="text-right">
                  {formatCount(row.resourceCount)}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {format(row.monthlyCost)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

/**
 * Cloud spend (User Story 87).
 *
 * Every figure here was typed by a person. The freshness banner is not
 * decoration — it is the difference between an honest number and one that
 * looks like live billing data.
 */
export const CloudCostTab: React.FC = () => {
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const [summary, setSummary] = useState<CloudCostSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await cloudAssetService.getCostSummary());
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to load cloud cost', 'error');
    } finally {
      setLoading(false);
    }
    // showToast comes from context and is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const asOf = summary?.oldestCostUpdatedAt
    ? new Date(summary.oldestCostUpdatedAt).toLocaleDateString()
    : null;

  // Aggregates arrive already expressed in the register's base currency, so
  // they are converted from that, not from the viewer's display currency.
  const money = (amount: number) => formatCost(amount, summary?.baseCurrency);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          title="Total Monthly Spend"
          value={money(summary?.totalMonthlyCost ?? 0)}
          subtitle={`across ${formatCount(summary?.resourcesWithCost ?? 0)} resource(s) with a cost recorded`}
          icon={Wallet}
          iconColor="bg-sky-500/10 text-sky-500"
          loading={loading}
        />
        <StatCard
          title="No Cost Recorded"
          value={formatCount(summary?.resourcesWithoutCost ?? 0)}
          subtitle="these resources contribute nothing to the total"
          icon={FileQuestion}
          iconColor="bg-amber-500/10 text-amber-500"
          loading={loading}
        />
        <StatCard
          title="Stale Figures"
          value={formatCount(summary?.staleCount ?? 0)}
          subtitle="not updated in over 90 days"
          icon={History}
          iconColor="bg-rose-500/10 text-rose-500"
          loading={loading}
        />
      </div>

      {!loading && summary && (
        <>
          {/* Non-optional: cost here is manually entered and must never read as live. */}
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <span className="font-medium">
                Cost figures are entered by hand, not synced.
              </span>{' '}
              {asOf ? (
                <>
                  The oldest figure in this total was last updated on{' '}
                  <strong>{asOf}</strong>. Treat the total as accurate only to that
                  date.
                </>
              ) : (
                <>No cost figures have been recorded yet.</>
              )}
            </div>
          </div>

          {summary.unconvertibleCurrencies.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div>
                <span className="font-medium">
                  {formatCount(summary.unconvertibleResourceCount)} resource(s)
                  excluded from the total.
                </span>{' '}
                No exchange rate is configured for:{' '}
                {summary.unconvertibleCurrencies.join(', ')}. Add the rate under
                System Settings — the spend above is understated until you do.
              </div>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownTable
              title="By Department"
              rows={summary.byDepartment}
              format={money}
            />
            <BreakdownTable
              title="By Environment"
              rows={summary.byEnvironment}
              format={money}
            />
            <BreakdownTable
              title="By Provider"
              rows={summary.byProvider}
              format={money}
            />
            <BreakdownTable
              title="By Resource Type"
              rows={summary.byResourceType}
              format={money}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CloudCostTab;
